/**
 * Attendance Point System Calculator
 * Two Sisters Cleaning & Organizing
 *
 * Points roll on a 90-day basis and expire automatically after 90 days.
 * System compares scheduled shift times vs actual clock-in/clock-out times.
 */

// ===== POINT TABLES =====

export const TARDINESS_POINTS = [
  { min: 1, max: 6, points: 0.25, label: '1-6 min late' },
  { min: 7, max: 30, points: 0.75, label: '7-30 min late' },
  { min: 31, max: 59, points: 1.25, label: '31-59 min late' },
  { min: 60, max: Infinity, points: 2.0, label: '60+ min late' },
];

export const LEAVING_EARLY_POINTS = [
  { min: 1, max: 6, points: 0.25, label: '1-6 min early' },
  { min: 7, max: 30, points: 0.75, label: '7-30 min early' },
  { min: 31, max: 59, points: 1.25, label: '31-59 min early' },
  { min: 60, max: Infinity, points: 2.0, label: '60+ min early' },
];

// Call-out points: [withPTO, withoutPTO]
export const CALLOUT_POINTS = {
  'week_plus':       { withPTO: 0, withoutPTO: 0.5, label: '1 week+ notice' },
  '72hr_to_week':    { withPTO: 0.5, withoutPTO: 1.0, label: '72hr - 1 week notice' },
  '24_to_72hr':      { withPTO: 1.0, withoutPTO: 1.5, label: '24 - 72hr notice' },
  'under_24hr_excused':   { withPTO: 1.5, withoutPTO: 2.0, label: '<24hr (excused)' },
  'under_24hr_unexcused': { withPTO: 3.0, withoutPTO: 4.0, label: '<24hr (unexcused)' },
  'after_shift_start':    { withPTO: 4.0, withoutPTO: 5.0, label: 'After shift starts' },
  'no_call_no_show':      { withPTO: 7.0, withoutPTO: 7.0, label: 'No call / no show' },
};

// Manager-approved leaving early = 0 points
export const MANAGER_APPROVED_EARLY_LEAVE = 0;

// ===== WARNING THRESHOLDS =====

export const THRESHOLDS = [
  { points: 3, level: 'verbal_coaching', label: 'Verbal Coaching', color: '#f59e0b', bgColor: '#fef3c7' },
  { points: 4, level: 'no_bonus', label: 'No Bonus Eligibility', color: '#f97316', bgColor: '#ffedd5' },
  { points: 5, level: 'written_warning', label: 'Written Warning', color: '#ef4444', bgColor: '#fee2e2' },
  { points: 7, level: 'final_warning', label: 'Final Warning', color: '#dc2626', bgColor: '#fecaca' },
  { points: 8, level: 'termination', label: 'Termination', color: '#991b1b', bgColor: '#fca5a5' },
];

// ===== POINT REDUCTIONS =====

export const POINT_REDUCTIONS = {
  thirtyDaysPerfect: -0.5,
  sixtyDaysPerfect: -1.0,
  maxReductionPer90Days: -1.0,
};

// ===== EXCUSED REASONS =====

export const EXCUSED_REASONS = [
  'Illness',
  'Bereavement',
  'Jury duty',
  'Documented medical',
  'True emergency',
];

export const UNEXCUSED_REASONS = [
  'Personal errands',
  'Oversleeping',
  'Transportation',
  'Childcare (non-emergency)',
];

// ===== CALCULATOR FUNCTIONS =====

/**
 * Calculate tardiness points based on minutes late
 */
export function calculateTardinessPoints(minutesLate) {
  if (minutesLate <= 0) return null;
  for (const tier of TARDINESS_POINTS) {
    if (minutesLate >= tier.min && minutesLate <= tier.max) {
      return { points: tier.points, label: tier.label, minutes: minutesLate };
    }
  }
  return null;
}

/**
 * Calculate early departure points based on minutes early
 */
export function calculateEarlyLeavePoints(minutesEarly, managerApproved = false) {
  if (minutesEarly <= 0 || managerApproved) return null;
  for (const tier of LEAVING_EARLY_POINTS) {
    if (minutesEarly >= tier.min && minutesEarly <= tier.max) {
      return { points: tier.points, label: tier.label, minutes: minutesEarly };
    }
  }
  return null;
}

/**
 * Calculate call-out points based on notice period and PTO usage
 */
export function calculateCalloutPoints(noticeCategory, hasPTO) {
  const tier = CALLOUT_POINTS[noticeCategory];
  if (!tier) return null;
  const points = hasPTO ? tier.withPTO : tier.withoutPTO;
  return { points, label: tier.label, hasPTO };
}

/**
 * Parse time string (HH:mm) into minutes since midnight
 */
function timeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * Compare scheduled shift start vs clock-in time
 * Returns minutes late (positive) or minutes early (negative)
 */
export function compareClockInToShift(scheduledStart, clockInTime) {
  const scheduledMinutes = timeToMinutes(scheduledStart);
  if (scheduledMinutes === null) return 0;

  // clockInTime is an ISO string, extract HH:mm
  const clockIn = new Date(clockInTime);
  const clockInMinutes = clockIn.getHours() * 60 + clockIn.getMinutes();

  return clockInMinutes - scheduledMinutes;
}

/**
 * Compare clock-out time vs scheduled shift end
 * Returns minutes early (positive) or minutes late staying (negative)
 */
export function compareClockOutToShift(scheduledEnd, clockOutTime) {
  const scheduledMinutes = timeToMinutes(scheduledEnd);
  if (scheduledMinutes === null) return 0;

  const clockOut = new Date(clockOutTime);
  const clockOutMinutes = clockOut.getHours() * 60 + clockOut.getMinutes();

  return scheduledMinutes - clockOutMinutes;
}

/**
 * Auto-detect points from a time record + shift
 * Returns an array of point entries to create
 */
export function autoDetectPoints(timeRecord, shift) {
  const points = [];

  if (!timeRecord || !shift) return points;

  // Check tardiness
  if (timeRecord.clockInTime && shift.startTime) {
    const minutesLate = compareClockInToShift(shift.startTime, timeRecord.clockInTime);
    if (minutesLate > 0) {
      const tardiness = calculateTardinessPoints(minutesLate);
      if (tardiness) {
        points.push({
          type: 'tardiness',
          points: tardiness.points,
          description: tardiness.label,
          minutesLate: tardiness.minutes,
          scheduledTime: shift.startTime,
          actualTime: timeRecord.clockInTime,
        });
      }
    }
  }

  // Early departures are not auto-detected — they must be manually logged by a manager.

  return points;
}

/**
 * Filter points to only include those within the 90-day rolling window
 */
export function getActivePoints(pointEntries, referenceDate = new Date()) {
  const cutoff = new Date(referenceDate);
  cutoff.setDate(cutoff.getDate() - 90);
  const cutoffStr = cutoff.toISOString();

  return pointEntries.filter(p => {
    const pointDate = p.date || p.createdAt;
    return pointDate >= cutoffStr && !p.voided;
  });
}

/**
 * Calculate total active points for an employee
 */
export function calculateTotalPoints(pointEntries, referenceDate = new Date()) {
  const active = getActivePoints(pointEntries, referenceDate);
  return active.reduce((sum, p) => sum + (p.points || 0), 0);
}

/**
 * Get current warning level based on total points
 */
export function getWarningLevel(totalPoints) {
  let level = null;
  for (const threshold of THRESHOLDS) {
    if (totalPoints >= threshold.points) {
      level = threshold;
    }
  }
  return level;
}

/**
 * Get the next threshold the employee is approaching
 */
export function getNextThreshold(totalPoints) {
  for (const threshold of THRESHOLDS) {
    if (totalPoints < threshold.points) {
      return {
        ...threshold,
        pointsAway: Math.round((threshold.points - totalPoints) * 100) / 100,
      };
    }
  }
  return null;
}

/**
 * Calculate perfect attendance streaks and applicable reductions
 */
export function calculateReductions(pointEntries, referenceDate = new Date()) {
  // Find the most recent point entry date
  const active = getActivePoints(pointEntries, referenceDate);
  if (active.length === 0) return { eligible: false, streakDays: 0, reduction: 0 };

  // Sort by date descending
  const sorted = [...active]
    .filter(p => p.points > 0) // only infractions
    .sort((a, b) => (b.date || b.createdAt).localeCompare(a.date || a.createdAt));

  if (sorted.length === 0) return { eligible: false, streakDays: 0, reduction: 0 };

  // Days since last infraction
  const lastInfraction = new Date(sorted[0].date || sorted[0].createdAt);
  const daysSince = Math.floor((referenceDate - lastInfraction) / (1000 * 60 * 60 * 24));

  let reduction = 0;
  if (daysSince >= 60) {
    reduction = POINT_REDUCTIONS.sixtyDaysPerfect;
  } else if (daysSince >= 30) {
    reduction = POINT_REDUCTIONS.thirtyDaysPerfect;
  }

  // Cap at max per 90 days
  const existingReductions = active
    .filter(p => p.type === 'reduction')
    .reduce((sum, p) => sum + p.points, 0);

  const remainingAllowance = POINT_REDUCTIONS.maxReductionPer90Days - existingReductions;
  reduction = Math.max(reduction, remainingAllowance);

  return {
    eligible: reduction < 0,
    streakDays: daysSince,
    reduction,
  };
}

/**
 * Format points for display with sign
 */
export function formatPoints(points) {
  if (points > 0) return `+${points}`;
  if (points < 0) return `${points}`;
  return '0';
}

/**
 * Get a human-readable summary for an employee
 */
export function getEmployeeSummary(pointEntries, referenceDate = new Date()) {
  const active = getActivePoints(pointEntries, referenceDate);
  const totalPoints = calculateTotalPoints(pointEntries, referenceDate);
  const warningLevel = getWarningLevel(totalPoints);
  const nextThreshold = getNextThreshold(totalPoints);

  const infractions = active.filter(p => p.points > 0);
  const reductions = active.filter(p => p.points < 0);

  return {
    totalPoints: Math.round(totalPoints * 100) / 100,
    activeEntries: active.length,
    infractions: infractions.length,
    reductions: reductions.length,
    warningLevel,
    nextThreshold,
    status: totalPoints >= 8 ? 'critical'
          : totalPoints >= 5 ? 'warning'
          : totalPoints >= 3 ? 'caution'
          : 'good',
  };
}

/**
 * Point entry types for display
 */
export const POINT_TYPES = {
  tardiness: { label: 'Tardiness', icon: 'clock', color: '#f59e0b' },
  early_leave: { label: 'Left Early', icon: 'log-out', color: '#f97316' },
  callout: { label: 'Call Out', icon: 'phone-off', color: '#ef4444' },
  no_call_no_show: { label: 'No Call / No Show', icon: 'alert-triangle', color: '#991b1b' },
  reduction: { label: 'Point Reduction', icon: 'trending-down', color: '#10b981' },
  manager_override: { label: 'Manager Adjustment', icon: 'shield', color: '#6366f1' },
};
